import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '@services/api';
import { useTranslationStore } from '@stores/translationStore';
import LoadingSpinner from '@components/LoadingSpinner';

interface OrderStatus {
  orderId: string;
  status: string;
  statusChangedAt: string;
  kitchenNotes?: string;
}

export default function OrderStatusPage() {
  const { hotelSlug, orderId } = useParams<{ hotelSlug: string; orderId: string }>();
  const { t } = useTranslationStore();
  const [order, setOrder] = useState<OrderStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    if (hotelSlug && orderId) {
      fetchOrderStatus(controller.signal);
    }
    return () => controller.abort();
  }, [hotelSlug, orderId]);

  async function fetchOrderStatus(signal?: AbortSignal) {
    setIsLoading(true);
    try {
      const response = await apiClient.get(`/api/v1/${hotelSlug}/orders/${orderId}/status`, { signal });
      if (response.data.success) {
        setOrder(response.data.data);
      }
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      setError((err as Error)?.message || 'Failed to load order status');
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">Order not found</p>
      </div>
    );
  }

  const statusConfig: Record<string, { icon: string; className: string; label: string }> = {
    pending: { icon: '⏳', className: 'pending', label: t('order.status.pending') || 'Pending' },
    confirmed: { icon: '✓', className: 'pending', label: t('order.status.confirmed') || 'Confirmed' },
    preparing: { icon: '👨‍🍳', className: 'preparing', label: t('order.status.preparing') || 'Preparing' },
    ready: { icon: '🔔', className: 'ready', label: t('order.status.ready') || 'Ready' },
    delivered: { icon: '✅', className: 'ready', label: t('order.status.delivered') || 'Delivered' },
  };

  const config = statusConfig[order.status] || statusConfig.pending;

  return (
    <div className="order-status animate-slide-up" aria-live="polite">
      <div className={`status-icon ${config.className}`}>
        {config.icon}
      </div>
      
      <h2 className="h4 mb-2">{config.label}</h2>
      
      <p className="text-muted mb-4">
        {t('order.order_number')}: {order.orderId.slice(0, 8).toUpperCase()}
      </p>
      
      {order.kitchenNotes && (
        <div className="alert alert-info">
          {order.kitchenNotes}
        </div>
      )}
      
      <p className="text-muted small">
        {t('order.last_updated')}: {new Date(order.statusChangedAt).toLocaleTimeString()}
      </p>
      
      <button 
        className="btn btn-outline-primary mt-3"
        onClick={() => fetchOrderStatus()}
      >
        {t('common.refresh')}
      </button>
    </div>
  );
}
